import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Add,
  Edit,
  Delete,
  Close,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_active: boolean;
  date_joined: string;
  last_login: string | null;
  role: string;
  scheme_access?: string[];
}

interface Scheme {
  id: string;
  name: string;
  code: string;
}

export default function UsersSettings() {
  const { access } = useAuth();
  const headers = { Authorization: `Bearer ${access}` };

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Schemes state (for SCHEME_VIEWER role)
  const [schemes, setSchemes] = useState<Scheme[]>([]);
  const [selectedSchemes, setSelectedSchemes] = useState<Scheme[]>([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    is_active: true,
    role: 'STANDARD',
  });

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  useEffect(() => {
    fetchUsers();
    fetchSchemes();
  }, []);

  const fetchSchemes = async () => {
    try {
      const response = await api.get('/schemes/', { headers });
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);
      setSchemes(data);
    } catch (err) {
      console.error('Failed to fetch schemes:', err);
    }
  };

  const fetchUserSchemeAccess = async (userId: string) => {
    try {
      const response = await api.get(`/users/${userId}/scheme-access/`, { headers });
      const schemeIds = response.data.map((access: any) => access.scheme);
      const userSchemes = schemes.filter((s) => schemeIds.includes(s.id));
      setSelectedSchemes(userSchemes);
    } catch (err) {
      console.error('Failed to fetch user scheme access:', err);
      setSelectedSchemes([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/', { headers });
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setDialogMode('create');
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      is_active: true,
      role: 'STANDARD',
    });
    setSelectedSchemes([]);
    setDialogOpen(true);
  };

  const handleEdit = async (user: User) => {
    setDialogMode('edit');
    setEditingUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '', // Don't pre-fill password
      is_active: user.is_active,
      role: user.role || 'STANDARD',
    });
    setSelectedSchemes([]);
    setDialogOpen(true);

    // Fetch scheme access if SCHEME_VIEWER
    if (user.role === 'SCHEME_VIEWER') {
      await fetchUserSchemeAccess(user.id);
    }
  };

  const handleSubmit = async () => {
    if (!formData.email) {
      setError('Email is required');
      return;
    }

    if (dialogMode === 'create' && !formData.password) {
      setError('Password is required when creating a new user');
      return;
    }

    // Validate SCHEME_VIEWER has at least one scheme selected
    if (formData.role === 'SCHEME_VIEWER' && selectedSchemes.length === 0) {
      setError('Scheme Viewers must have at least one scheme selected');
      return;
    }

    try {
      let submitData: any;

      if (dialogMode === 'edit' && !formData.password) {
        // Exclude password if empty during edit
        const { password: _password, ...dataWithoutPassword } = formData;
        submitData = dataWithoutPassword;
      } else {
        submitData = { ...formData };
      }

      let userId: string;

      if (dialogMode === 'create') {
        const response = await api.post('/users/', submitData, { headers });
        userId = response.data.id;
      } else if (editingUser) {
        await api.patch(`/users/${editingUser.id}/`, submitData, { headers });
        userId = editingUser.id;
      } else {
        throw new Error('No user to update');
      }

      // Update scheme access for SCHEME_VIEWER role
      if (formData.role === 'SCHEME_VIEWER') {
        await api.put(`/users/${userId}/scheme-access/`, {
          scheme_ids: selectedSchemes.map((s) => s.id),
        }, { headers });
      } else if (dialogMode === 'edit' && editingUser?.role === 'SCHEME_VIEWER') {
        // Clear scheme access if role changed from SCHEME_VIEWER
        await api.put(`/users/${userId}/scheme-access/`, {
          scheme_ids: [],
        }, { headers });
      }

      setDialogOpen(false);
      setEditingUser(null);
      setError('');
      await fetchUsers();
    } catch (err: any) {
      console.error('Failed to save user:', err);
      setError(err.response?.data?.detail || err.response?.data?.username?.[0] || err.response?.data?.email?.[0] || 'Failed to save user');
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await api.delete(`/users/${userToDelete.id}/`, { headers });
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      await fetchUsers();
    } catch (err: any) {
      console.error('Failed to remove user:', err);
      setError(err.response?.data?.detail || 'Failed to remove user from account');
      setDeleteConfirmOpen(false);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const columns: GridColDef<User>[] = [
    {
      field: 'full_name',
      headerName: 'Name',
      flex: 1.5,
      minWidth: 180,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 150,
      renderCell: (params) => {
        const roleLabels: Record<string, string> = {
          'STANDARD': 'Standard User',
          'CASE_REVIEWER': 'Case Reviewer',
          'SCHEME_VIEWER': 'Scheme Viewer',
        };
        const roleColors: Record<string, 'default' | 'info' | 'secondary'> = {
          'STANDARD': 'default',
          'CASE_REVIEWER': 'info',
          'SCHEME_VIEWER': 'secondary',
        };
        return (
          <Chip
            label={roleLabels[params.value] || params.value}
            size="small"
            color={roleColors[params.value] || 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Chip
            icon={<CheckCircle fontSize="small" />}
            label="Active"
            size="small"
            color="success"
            variant="outlined"
          />
        ) : (
          <Chip
            icon={<Cancel fontSize="small" />}
            label="Inactive"
            size="small"
            color="default"
            variant="outlined"
          />
        )
      ),
    },
    {
      field: 'date_joined',
      headerName: 'Joined',
      width: 130,
      valueGetter: (value) => {
        if (!value) return '-';
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            color="error"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">Users</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user accounts for your organization
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Users have login credentials and can access the EliotNest platform. All users are automatically added as contacts.
      </Alert>

      <DataGrid
        rows={users}
        columns={columns}
        loading={loading}
        autoHeight
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#616161',
            borderBottom: 'none',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#616161',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.95rem',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              '& .MuiIconButton-root': {
                color: 'white',
              },
            },
            '& .MuiDataGrid-sortIcon': {
              color: 'white',
            },
            '& .MuiDataGrid-menuIcon': {
              color: 'white',
            },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid',
            borderTopColor: 'divider',
          },
        }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add User' : 'Edit User'}
          <IconButton
            onClick={() => setDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              disabled={dialogMode === 'edit'}
              helperText={dialogMode === 'edit' ? 'Email cannot be changed' : 'Used for login and as username'}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
              />
            </Box>

            <TextField
              fullWidth
              label="Password"
              type="password"
              required={dialogMode === 'create'}
              value={formData.password}
              onChange={(e) => handleFormChange('password', e.target.value)}
              helperText={dialogMode === 'edit' ? 'Leave blank to keep current password' : 'Required for new users'}
            />

            <FormControl fullWidth>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={formData.role}
                label="Role"
                onChange={(e) => handleFormChange('role', e.target.value)}
              >
                <MenuItem value="STANDARD">Standard User</MenuItem>
                <MenuItem value="CASE_REVIEWER">Case Reviewer</MenuItem>
                <MenuItem value="SCHEME_VIEWER">Scheme Viewer</MenuItem>
              </Select>
            </FormControl>

            {/* Scheme Access selector - only shown for SCHEME_VIEWER role */}
            {formData.role === 'SCHEME_VIEWER' && (
              <Autocomplete
                multiple
                id="scheme-access-select"
                options={schemes}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                value={selectedSchemes}
                onChange={(_event, newValue) => setSelectedSchemes(newValue)}
                renderOption={(props, option, { selected }) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Accessible Schemes"
                    placeholder="Select schemes..."
                    helperText="Select which schemes this user can view"
                  />
                )}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Remove User
          <IconButton
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {userToDelete && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to remove this user from your account?
              </Alert>
              <Typography variant="body1">
                <strong>{userToDelete.full_name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userToDelete.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Note: This will remove the user's access to your account, but will not delete their user account.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}